import { Component, inject, OnInit, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Observable, Subscription, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { CouponService, Coupon } from '../services/coupon.service';
import { StoreService } from '../services/store.service';
import { BrandService } from '../services/brand.service';
import { AccessService, AppUser, PagePermission } from '../services/access.service';
import { ALL_BRANDS_ID, NO_BRAND_SELECTED, brandIssuerName, brandScopeLabel, eligibleCouponBrands, legacyBrandScopeNotice, selectableBrandValue, validateIssuanceBrandScope } from '../services/coupon-brand.util';
import { Auth, onAuthStateChanged, Unsubscribe } from '@angular/fire/auth';
import * as QRCode from 'qrcode';
import JSZip from 'jszip';

@Component({
  selector: 'app-coupon-management',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './coupon-management.component.html',
  styleUrls: ['./coupon-management.component.css']
})
export class CouponManagementComponent implements OnInit, OnDestroy {
  private couponService = inject(CouponService);
  private storeService = inject(StoreService);
  private brandService = inject(BrandService);
  private accessService = inject(AccessService);
  private auth = inject(Auth);

  coupons$: Observable<Coupon[]>;
  allCoupons: Coupon[] = [];
  filteredCoupons: Coupon[] = [];

  userRole: string | null = null;
  permissions: PagePermission[] = [];
  private authUnsubscribe?: Unsubscribe;

  get canCreateCoupons(): boolean {
    if (!this.userRole) return false;
    const role = this.userRole.toLowerCase().trim();
    if (role === 'superadmin') return true;
    const perm = this.permissions.find(p => p.id === 'coupon-management_create-coupons');
    return perm ? perm.roles.map(r => r.toLowerCase().trim()).includes(role) : false;
  }

  stores: any[] = [];
  brands: any[] = [];

  // Single Coupon Form
  newCoupon: Partial<Coupon> = {
    title: '',
    discountType: 'free_session',
    discountValue: 0,
    usageType: 'single_use',
    maxRedemptions: 1,
    applicableStores: ['all'],
    applicableBrands: [],            // brand scope must be chosen explicitly (KAN-227): never defaults to 'all'
    recipientName: '',
    recipientEmail: '',
    expiresAt: '',
    isActive: true
  };
  editingId: string | null = null;
  sendEmailOnCreate = false;

  // Single-choice brand picker (KAN-363), shared by the single and bulk forms: a brand id, 'all', or '' (nothing chosen)
  readonly allBrandsId = ALL_BRANDS_ID;
  selectedBrand: Record<string, string> = { single: NO_BRAND_SELECTED, bulk: NO_BRAND_SELECTED };
  brandTouched: Record<string, boolean> = { single: false, bulk: false };
  legacyBrandNotice: string | null = null;
  legacyBrandCurrent = '';

  // Bulk Generation Form
  bulkForm: {
    title: string;
    discountType: 'fixed' | 'percent' | 'free_session';
    discountValue: number;
    usageType: 'single_use' | 'multi_use';
    maxRedemptions: number;
    count: number;
    expiresAt: string;
    applicableBrands: string[];
  } = {
    title: 'Promotional Voucher',
    discountType: 'free_session',
    discountValue: 0,
    usageType: 'single_use',
    maxRedemptions: 1,
    count: 10,
    expiresAt: '',
    applicableBrands: []
  };

  // Search & Filter
  searchTerm = '';
  statusFilter = 'all';
  typeFilter = 'all';

  // Sorting
  sortColumn = 'issuedAt';
  sortDirection: 'asc' | 'desc' = 'desc';

  // Modals
  isModalOpen = false;
  isBulkModalOpen = false;
  isQrModalOpen = false;
  selectedCouponForQr: Coupon | null = null;
  qrDataUrl: string | null = null;

  // Email Modal (Centered Popup)
  isEmailModalOpen = false;
  selectedCouponForEmail: Coupon | null = null;
  emailModalRecipientName = '';
  emailModalRecipientEmail = '';
  emailModalSubject = '';
  emailModalTitle = '';
  emailModalBody = '';
  emailModalError: string | null = null;
  emailModalSuccess: string | null = null;

  // Redemption Details Modal
  isRedemptionModalOpen = false;
  selectedCouponForRedemption: Coupon | null = null;

  // Selection & Bulk Actions
  selectedCouponCodes: Set<string> = new Set<string>();
  isDownloadingZip = false;
  zipProgressText = '';

  // Status & Progress
  isSaving = false;
  isSendingEmail = false;
  errorMessage: string | null = null;
  copiedCode: string | null = null;

  private couponsSub: Subscription | null = null;

  constructor() {
    this.coupons$ = this.couponService.getCoupons().pipe(
      map(coupons => {
        this.allCoupons = coupons;
        this.applyFilterAndSort();
        return coupons;
      })
    );
  }

  ngOnInit() {
    this.couponsSub = this.coupons$.subscribe();
    this.storeService.getStores().subscribe(stores => this.stores = stores);
    this.brandService.getBrands().subscribe(brands => this.brands = brands);

    this.authUnsubscribe = onAuthStateChanged(this.auth, async (user) => {
      if (user) {
        const profile = await firstValueFrom(this.accessService.getUserProfile(user.uid));
        this.userRole = profile?.role || null;
        this.accessService.getPagePermissions().subscribe(perms => {
          this.permissions = perms;
        });
      } else {
        this.userRole = null;
        this.permissions = [];
      }
    });
  }

  ngOnDestroy() {
    this.couponsSub?.unsubscribe();
    if (this.authUnsubscribe) {
      this.authUnsubscribe();
    }
  }

  // --- Filter & Sort ---
  applyFilterAndSort() {
    let result = [...this.allCoupons];

    if (this.searchTerm.trim()) {
      const term = this.searchTerm.trim().toLowerCase();
      result = result.filter(c => 
        (c.code && c.code.toLowerCase().includes(term)) ||
        (c.title && c.title.toLowerCase().includes(term)) ||
        (c.recipientName && c.recipientName.toLowerCase().includes(term)) ||
        (c.recipientEmail && c.recipientEmail.toLowerCase().includes(term)) ||
        this.getBrandLabel(c).toLowerCase().includes(term)
      );
    }

    if (this.statusFilter !== 'all') {
      result = result.filter(c => {
        const isExp = this.isCouponExpired(c);
        if (this.statusFilter === 'active') return c.isActive && !isExp && (c.usageType === 'multi_use' || c.timesRedeemed < c.maxRedemptions);
        if (this.statusFilter === 'redeemed') return c.timesRedeemed >= c.maxRedemptions;
        if (this.statusFilter === 'expired') return isExp;
        if (this.statusFilter === 'inactive') return !c.isActive;
        return true;
      });
    }

    if (this.typeFilter !== 'all') {
      result = result.filter(c => c.discountType === this.typeFilter);
    }

    // Sort
    result.sort((a: any, b: any) => {
      const isBrandSort = this.sortColumn === 'brands';
      let valA = isBrandSort ? this.getBrandLabel(a) : a[this.sortColumn];
      let valB = isBrandSort ? this.getBrandLabel(b) : b[this.sortColumn];

      if (valA == null && valB == null) return 0;
      if (valA == null) return -1;
      if (valB == null) return 1;

      if (typeof valA === 'number' && typeof valB === 'number') {
        return this.sortDirection === 'asc' ? valA - valB : valB - valA;
      }
      return this.sortDirection === 'asc' 
        ? String(valA).localeCompare(String(valB)) 
        : String(valB).localeCompare(String(valA));
    });

    this.filteredCoupons = result;
  }

  onSort(column: string) {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.applyFilterAndSort();
  }

  // --- CRUD Single Coupon ---
  openAddModal() {
    this.cancelEdit();
    this.isModalOpen = true;
  }

  async saveCoupon() {
    this.isSaving = true;
    this.errorMessage = null;

    if (!this.newCoupon.title) {
      this.errorMessage = 'Please enter a title or promotion description.';
      this.isSaving = false;
      return;
    }

    this.newCoupon.applicableBrands = this.scopeFromSelection('single');
    const brandError = validateIssuanceBrandScope(this.newCoupon.applicableBrands, this.brands);
    if (brandError) {
      this.brandTouched['single'] = true;
      this.errorMessage = null;
      this.isSaving = false;
      this.focusBrandPicker('single');
      return;
    }
    const brandScope = this.newCoupon.applicableBrands;

    try {
      const pacificExpiresAt = this.toPacificEndOfDayIso(this.newCoupon.expiresAt);

      if (this.editingId) {
        await this.couponService.updateCoupon(this.editingId, {
          title: this.newCoupon.title,
          applicableBrands: brandScope,
          discountType: this.newCoupon.discountType,
          discountValue: this.newCoupon.discountType === 'free_session' ? 0 : Number(this.newCoupon.discountValue || 0),
          recipientName: this.newCoupon.recipientName || '',
          recipientEmail: this.newCoupon.recipientEmail || '',
          expiresAt: pacificExpiresAt || null,
          usageType: this.newCoupon.usageType || 'single_use',
          maxRedemptions: this.newCoupon.usageType === 'single_use' ? 1 : Number(this.newCoupon.maxRedemptions || 1),
          isActive: this.newCoupon.isActive !== false
        });
      } else {
        const generatedCode = await this.couponService.addCoupon({
          ...this.newCoupon,
          applicableBrands: brandScope,
          expiresAt: pacificExpiresAt || null,
          discountValue: this.newCoupon.discountType === 'free_session' ? 0 : Number(this.newCoupon.discountValue || 0),
          maxRedemptions: this.newCoupon.usageType === 'single_use' ? 1 : Number(this.newCoupon.maxRedemptions || 1)
        });

        if (this.sendEmailOnCreate && this.newCoupon.recipientEmail) {
          const createdCoupon: Coupon = {
            code: generatedCode,
            title: this.newCoupon.title!,
            discountType: this.newCoupon.discountType!,
            discountValue: this.newCoupon.discountType === 'free_session' ? 0 : Number(this.newCoupon.discountValue || 0),
            applicableBrands: brandScope,
            recipientName: this.newCoupon.recipientName,
            recipientEmail: this.newCoupon.recipientEmail,
            expiresAt: pacificExpiresAt || null,
            usageType: this.newCoupon.usageType || 'single_use',
            maxRedemptions: this.newCoupon.usageType === 'single_use' ? 1 : Number(this.newCoupon.maxRedemptions || 1),
            timesRedeemed: 0,
            status: 'active',
            isActive: true,
            issuedAt: new Date().toISOString()
          };
          await this.couponService.sendCouponEmail(createdCoupon);
        }
      }

      this.cancelEdit();
    } catch (err: any) {
      console.error(err);
      this.errorMessage = err.message || 'An error occurred while saving.';
    } finally {
      this.isSaving = false;
    }
  }

  editCoupon(coupon: Coupon) {
    this.editingId = coupon.code;
    this.newCoupon = {
      ...coupon,
      // A legacy coupon (multi-brand, LFC, unknown or unset) opens with nothing selected: saving it requires an explicit choice.
      applicableBrands: [],
      expiresAt: coupon.expiresAt ? this.formatDateForInput(coupon.expiresAt) : ''
    };
    this.selectedBrand['single'] = selectableBrandValue(coupon.applicableBrands, this.brands);
    this.brandTouched['single'] = false;
    this.legacyBrandNotice = legacyBrandScopeNotice(coupon.applicableBrands, this.brands)
      ?? (!coupon.applicableBrands?.length ? 'This coupon has no brand yet. Pick a brand or All brands before saving.' : null);
    this.legacyBrandCurrent = this.legacyBrandNotice ? this.getBrandLabel(coupon) : '';
    this.sendEmailOnCreate = false;
    this.errorMessage = null;
    this.isModalOpen = true;
  }

  cancelEdit() {
    this.newCoupon = {
      title: '',
      discountType: 'free_session',
      discountValue: 0,
      usageType: 'single_use',
      maxRedemptions: 1,
      applicableStores: ['all'],
      applicableBrands: [],
      recipientName: '',
      recipientEmail: '',
      expiresAt: '',
      isActive: true
    };
    this.resetBrandPicker('single');
    this.editingId = null;
    this.sendEmailOnCreate = false;
    this.errorMessage = null;
    this.isModalOpen = false;
  }

  async deleteCoupon(coupon: Coupon) {
    if (confirm(`Are you sure you want to delete coupon ${this.formatCode(coupon.code)}?`)) {
      await this.couponService.deleteCoupon(coupon.code);
    }
  }

  // --- Bulk Generation ---
  openBulkModal() {
    this.bulkForm = {
      title: 'Promotional Voucher',
      discountType: 'free_session',
      discountValue: 0,
      usageType: 'single_use',
      maxRedemptions: 1,
      count: 10,
      expiresAt: '',
      applicableBrands: []
    };
    this.resetBrandPicker('bulk');
    this.errorMessage = null;
    this.isBulkModalOpen = true;
  }

  closeBulkModal() {
    this.isBulkModalOpen = false;
    this.errorMessage = null;
  }

  async generateBulkCoupons() {
    if (this.bulkForm.count <= 0 || this.bulkForm.count > 200) {
      this.errorMessage = 'Please enter a count between 1 and 200.';
      return;
    }

    this.bulkForm.applicableBrands = this.scopeFromSelection('bulk');
    const brandError = validateIssuanceBrandScope(this.bulkForm.applicableBrands, this.brands);
    if (brandError) {
      this.brandTouched['bulk'] = true;
      this.focusBrandPicker('bulk');
      return;
    }

    this.isSaving = true;
    this.errorMessage = null;

    try {
      const pacificExpiresAt = this.toPacificEndOfDayIso(this.bulkForm.expiresAt);

      await this.couponService.addBulkCoupons({
        title: this.bulkForm.title,
        applicableBrands: this.bulkForm.applicableBrands,
        discountType: this.bulkForm.discountType,
        discountValue: this.bulkForm.discountType === 'free_session' ? 0 : Number(this.bulkForm.discountValue || 0),
        usageType: this.bulkForm.usageType,
        maxRedemptions: this.bulkForm.usageType === 'single_use' ? 1 : Number(this.bulkForm.maxRedemptions || 1),
        expiresAt: pacificExpiresAt || null
      }, this.bulkForm.count);

      this.closeBulkModal();
    } catch (err: any) {
      console.error(err);
      this.errorMessage = err.message || 'Error generating bulk coupons.';
    } finally {
      this.isSaving = false;
    }
  }

  // --- QR Preview & Print ---
  async viewQrModal(coupon: Coupon) {
    this.selectedCouponForQr = coupon;
    try {
      this.qrDataUrl = await QRCode.toDataURL(coupon.code, {
        width: 320,
        margin: 2,
        color: { dark: '#000000', light: '#ffffff' }
      });
      this.isQrModalOpen = true;
    } catch (err) {
      console.error('Error generating QR', err);
      alert('Failed to generate QR preview.');
    }
  }

  closeQrModal() {
    this.isQrModalOpen = false;
    this.selectedCouponForQr = null;
    this.qrDataUrl = null;
  }

  async printVoucher() {
    if (this.selectedCouponForQr) {
      try {
        await this.couponService.updateCoupon(this.selectedCouponForQr.code, {
          recipientName: 'Printed',
          recipientEmail: ''
        });
        this.selectedCouponForQr.recipientName = 'Printed';
        this.selectedCouponForQr.recipientEmail = '';
      } catch (err) {
        console.error('Error updating printed status', err);
      }
    }
    window.print();
  }

  // --- Email Modal Popup (Center) Actions ---
  openEmailModal(coupon: Coupon) {
    this.selectedCouponForEmail = coupon;
    this.emailModalRecipientName = coupon.recipientName === 'Printed' ? '' : (coupon.recipientName || '');
    this.emailModalRecipientEmail = coupon.recipientEmail || '';
    this.emailModalTitle = coupon.title || '';
    this.emailModalSubject = `You have received a new Coupon from ${this.getIssuerName(coupon)}!`;
    this.emailModalBody = `You have received an exclusive photo booth voucher valid for ${this.getBrandLabel(coupon)}! Scan your QR code at the kiosk payment screen or enter your 12-digit code.`;
    this.emailModalError = null;
    this.emailModalSuccess = null;
    this.isEmailModalOpen = true;
  }

  closeEmailModal() {
    if (this.isSendingEmail) return;
    this.isEmailModalOpen = false;
    this.selectedCouponForEmail = null;
    this.emailModalError = null;
    this.emailModalSuccess = null;
  }

  getQrApiUrl(code: string): string {
    return `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(code)}`;
  }

  async sendEmailFromModal() {
    if (!this.selectedCouponForEmail) return;

    const email = this.emailModalRecipientEmail?.trim();
    if (!email || !email.includes('@') || !email.includes('.')) {
      this.emailModalError = 'Please enter a valid recipient email address.';
      return;
    }

    if (!this.emailModalTitle?.trim()) {
      this.emailModalError = 'Promotion title cannot be empty.';
      return;
    }

    this.isSendingEmail = true;
    this.emailModalError = null;
    this.emailModalSuccess = null;

    try {
      const couponToSend: Coupon = {
        ...this.selectedCouponForEmail,
        title: this.emailModalTitle.trim(),
        recipientName: this.emailModalRecipientName?.trim() || '',
        recipientEmail: email
      };

      await this.couponService.sendCouponEmail(couponToSend, {
        subject: this.emailModalSubject?.trim(),
        customTitle: this.emailModalTitle?.trim(),
        customBody: this.emailModalBody?.trim()
      });

      // Save recipient name & email and title back to Firestore document
      await this.couponService.updateCoupon(couponToSend.code, {
        title: couponToSend.title,
        recipientName: couponToSend.recipientName,
        recipientEmail: couponToSend.recipientEmail
      });

      this.emailModalSuccess = `Coupon email successfully sent to ${email}!`;

      setTimeout(() => {
        if (this.isEmailModalOpen) {
          this.closeEmailModal();
        }
      }, 1500);
    } catch (err: any) {
      console.error('Error sending coupon email:', err);
      this.emailModalError = err.message || 'Failed to send email. Please try again.';
    } finally {
      this.isSendingEmail = false;
    }
  }

  openEmailModalFromQrModal() {
    if (!this.selectedCouponForQr) return;
    const coupon = this.selectedCouponForQr;
    this.closeQrModal();
    this.openEmailModal(coupon);
  }

  // --- Brand scope (KAN-227) ---
  /** Brand text for tables, voucher, ZIP card and email: brand names from the brands collection, "All brands", or "Brand not set". */
  getBrandLabel(coupon: Partial<Coupon> | null | undefined): string {
    return brandScopeLabel(coupon?.applicableBrands, this.brands);
  }

  /** Name shown as the voucher/email brand header: the brand for a single-brand coupon, otherwise the multi-brand issuer name. */
  getIssuerName(coupon: Partial<Coupon> | null | undefined): string {
    return brandIssuerName(coupon?.applicableBrands, this.brands);
  }

  /** Brands offered by the picker (KAN-363): active, alphabetical, never the retired LFC brand. */
  get brandOptions(): any[] {
    return eligibleCouponBrands(this.brands);
  }

  /** Stored scope for the current radio choice: [brandId], ['all'], or [] when nothing is chosen. */
  private scopeFromSelection(target: 'single' | 'bulk'): string[] {
    const value = this.selectedBrand[target];
    return value ? [value] : [];
  }

  selectBrand(target: 'single' | 'bulk', value: string) {
    this.selectedBrand[target] = value;
    this.brandTouched[target] = true;
    this.errorMessage = null;
  }

  /** Inline brand error: only after a submit attempt or interaction, never before. */
  brandError(target: 'single' | 'bulk'): string | null {
    if (!this.brandTouched[target]) return null;
    return validateIssuanceBrandScope(this.scopeFromSelection(target), this.brands);
  }

  private resetBrandPicker(target: 'single' | 'bulk') {
    this.selectedBrand[target] = NO_BRAND_SELECTED;
    this.brandTouched[target] = false;
    if (target === 'single') {
      this.legacyBrandNotice = null;
      this.legacyBrandCurrent = '';
    }
  }

  /** Moves focus to the radio group so keyboard and screen-reader users land on the validation error. */
  private focusBrandPicker(target: 'single' | 'bulk') {
    setTimeout(() => document.getElementById('brand-fieldset-' + target)?.focus(), 0);
  }

  // --- Helpers ---
  formatCode(code: string): string {
    return this.couponService.formatDisplayCode(code);
  }

  copyCode(code: string) {
    navigator.clipboard.writeText(code);
    this.copiedCode = code;
    setTimeout(() => {
      if (this.copiedCode === code) this.copiedCode = null;
    }, 2000);
  }

  isCouponExpired(coupon: Coupon): boolean {
    if (!coupon.expiresAt) return false;
    const exp = new Date(coupon.expiresAt).getTime();
    return !isNaN(exp) && exp < Date.now();
  }

  getCouponStatus(coupon: Coupon): { label: string; class: string; isRedeemed: boolean } {
    const isRedeemed = coupon.status === 'redeemed' || 
                       (coupon.timesRedeemed != null && coupon.maxRedemptions != null && coupon.timesRedeemed >= coupon.maxRedemptions && coupon.timesRedeemed > 0) || 
                       Boolean(coupon.redeemedAt);

    if (isRedeemed) {
      return { label: 'Redeemed', class: 'redeemed', isRedeemed: true };
    }
    if (this.isCouponExpired(coupon)) {
      return { label: 'Expired', class: 'expired', isRedeemed: false };
    }
    if (!coupon.isActive || coupon.status === 'revoked') {
      return { label: 'Inactive', class: 'inactive', isRedeemed: false };
    }
    return { label: 'Active', class: 'active', isRedeemed: false };
  }

  openRedemptionDetails(coupon: Coupon) {
    this.selectedCouponForRedemption = coupon;
    this.isRedemptionModalOpen = true;
  }

  closeRedemptionModal() {
    this.isRedemptionModalOpen = false;
    this.selectedCouponForRedemption = null;
  }

  copiedSessionId = false;

  copySessionId(sessionId?: string) {
    if (!sessionId) return;
    navigator.clipboard.writeText(sessionId);
    this.copiedSessionId = true;
    setTimeout(() => {
      this.copiedSessionId = false;
    }, 2000);
  }

  getStoreNameOnly(storeId?: string): string {
    if (!storeId) return 'Not Specified';
    const trimmedId = storeId.trim();
    if (!trimmedId) return 'Not Specified';

    const normalizedTarget = trimmedId.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

    const found = this.stores.find(s => {
      const sId = (s.storeId || '').trim().toLowerCase();
      const id = (s.id || '').trim().toLowerCase();
      const name = (s.name || '').trim().toLowerCase();
      const sNorm = (s.storeId || '').replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
      const nameNorm = (s.name || '').replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

      return sId === trimmedId.toLowerCase() || 
             id === trimmedId.toLowerCase() || 
             name === trimmedId.toLowerCase() ||
             sNorm === normalizedTarget ||
             nameNorm === normalizedTarget;
    });

    if (found && found.name) {
      return found.name;
    }
    if (found && found.storeId) {
      return found.storeId;
    }

    return trimmedId;
  }

  getStoreDisplayName(storeId?: string): string {
    if (!storeId) return 'N/A';
    const trimmedId = storeId.trim();
    const found = this.stores.find(s => s.storeId === trimmedId || s.id === trimmedId || s.name === trimmedId);
    if (found) {
      return `${found.name || found.storeId} (${trimmedId})`;
    }
    return trimmedId;
  }

  getLocalTimeZoneName(val?: any): string {
    return 'Pacific Time (PT)';
  }

  formatLocalDateTime(val: any): string {
    if (!val) return 'N/A';
    const d = new Date(val);
    if (isNaN(d.getTime())) return String(val);
    return d.toLocaleString('en-US', { 
      timeZone: 'America/Los_Angeles',
      year: 'numeric', 
      month: 'short', 
      day: 'numeric', 
      hour: 'numeric', 
      minute: '2-digit', 
      second: '2-digit',
      hour12: true
    }) + ' PT';
  }

  formatDateTime(val: any): string {
    if (!val) return 'N/A';
    const d = new Date(val);
    if (isNaN(d.getTime())) return String(val);
    return d.toLocaleString('en-US', { 
      timeZone: 'America/Los_Angeles',
      year: 'numeric', 
      month: 'short', 
      day: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit' 
    }) + ' PT';
  }

  getDiscountText(coupon: Partial<Coupon>): string {
    if (coupon.discountType === 'free_session') return 'Free Session';
    if (coupon.discountType === 'percent') return `${coupon.discountValue}% OFF`;
    return `$${Number(coupon.discountValue || 0).toFixed(2)} OFF`;
  }

  formatDate(val: any): string {
    if (!val) return 'Never';
    const d = new Date(val);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-US', { 
      timeZone: 'America/Los_Angeles',
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  }

  toPacificEndOfDayIso(dateStr?: string): string | null {
    if (!dateStr) return null;
    const trimmed = dateStr.trim();
    if (!trimmed) return null;
    if (trimmed.includes('T')) return trimmed;

    // Calculate Pacific Time offset for that specific date to handle Daylight Saving Time (PST vs PDT)
    const [year, month, day] = trimmed.split('-').map(Number);
    const testDate = new Date(Date.UTC(year, month - 1, day, 23, 59, 59));
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Los_Angeles',
      timeZoneName: 'shortOffset'
    }).formatToParts(testDate);
    const offsetPart = parts.find(p => p.type === 'timeZoneName')?.value;
    let offsetStr = '-08:00';
    if (offsetPart) {
      const match = offsetPart.match(/GMT([+-]\d+)/);
      if (match) {
        const hours = parseInt(match[1], 10);
        const sign = hours >= 0 ? '+' : '-';
        const absHours = Math.abs(hours).toString().padStart(2, '0');
        offsetStr = `${sign}${absHours}:00`;
      }
    }
    return `${trimmed}T23:59:59${offsetStr}`;
  }

  private formatDateForInput(val: any): string {
    if (!val) return '';
    const d = new Date(val);
    if (isNaN(d.getTime())) return '';
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Los_Angeles',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(d);
    const year = parts.find(p => p.type === 'year')?.value;
    const month = parts.find(p => p.type === 'month')?.value;
    const day = parts.find(p => p.type === 'day')?.value;
    return `${year}-${month}-${day}`;
  }

  // --- Selection & Checkbox Logic ---
  isAllSelected(): boolean {
    if (!this.filteredCoupons || this.filteredCoupons.length === 0) return false;
    return this.filteredCoupons.every(c => this.selectedCouponCodes.has(c.code));
  }

  isSomeSelected(): boolean {
    if (!this.filteredCoupons || this.filteredCoupons.length === 0) return false;
    const count = this.filteredCoupons.filter(c => this.selectedCouponCodes.has(c.code)).length;
    return count > 0 && count < this.filteredCoupons.length;
  }

  toggleSelectAll() {
    if (this.isAllSelected()) {
      this.selectedCouponCodes.clear();
    } else {
      this.filteredCoupons.forEach(c => this.selectedCouponCodes.add(c.code));
    }
  }

  toggleSelectCoupon(code: string, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    if (this.selectedCouponCodes.has(code)) {
      this.selectedCouponCodes.delete(code);
    } else {
      this.selectedCouponCodes.add(code);
    }
  }

  getSelectedCount(): number {
    return this.selectedCouponCodes.size;
  }

  clearSelection() {
    this.selectedCouponCodes.clear();
  }

  /**
   * Generates high-resolution PNG voucher card on an in-memory Canvas
   */
  private async renderVoucherCardToBlob(coupon: Coupon): Promise<Blob> {
    // Generate QR Data URL
    const qrDataUrl = await QRCode.toDataURL(coupon.code, {
      width: 400,
      margin: 2,
      color: { dark: '#000000', light: '#ffffff' }
    });

    // Create 800x1100 Canvas for crisp print quality
    const canvas = document.createElement('canvas');
    canvas.width = 800;
    canvas.height = 1100;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D context not available');

    // 1. Background
    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(0, 0, 800, 1100);

    // 2. Card Container (White with rounded corners & border)
    const cardX = 40;
    const cardY = 40;
    const cardW = 720;
    const cardH = 1020;
    const radius = 24;

    ctx.save();
    ctx.beginPath();
    ctx.moveTo(cardX + radius, cardY);
    ctx.lineTo(cardX + cardW - radius, cardY);
    ctx.quadraticCurveTo(cardX + cardW, cardY, cardX + cardW, cardY + radius);
    ctx.lineTo(cardX + cardW, cardY + cardH - radius);
    ctx.quadraticCurveTo(cardX + cardW, cardY + cardH, cardX + cardW - radius, cardY + cardH);
    ctx.lineTo(cardX + radius, cardY + cardH);
    ctx.quadraticCurveTo(cardX, cardY + cardH, cardX, cardY + cardH - radius);
    ctx.lineTo(cardX, cardY + radius);
    ctx.quadraticCurveTo(cardX, cardY, cardX + radius, cardY);
    ctx.closePath();

    ctx.fillStyle = '#ffffff';
    ctx.fill();
    ctx.lineWidth = 3;
    ctx.strokeStyle = '#e2e8f0';
    ctx.stroke();
    ctx.restore();

    // 3. Header Accent Bar
    ctx.save();
    ctx.fillStyle = '#2563eb';
    ctx.fillRect(cardX, cardY, cardW, 14);
    ctx.restore();

    // 4. Brand Name
    ctx.textAlign = 'center';
    ctx.fillStyle = '#1e293b';
    ctx.font = 'bold 36px "Segoe UI", Arial, sans-serif';
    ctx.fillText(this.getIssuerName(coupon), 400, 110);

    // 5. Title / Description
    ctx.fillStyle = '#64748b';
    ctx.font = '500 20px "Segoe UI", Arial, sans-serif';
    const displayTitle = coupon.title || 'Promotional Voucher';
    ctx.fillText(displayTitle, 400, 145);

    // 6. Discount Banner Box
    const bannerX = 80;
    const bannerY = 175;
    const bannerW = 640;
    const bannerH = 100;
    const bannerR = 16;

    ctx.save();
    ctx.beginPath();
    ctx.moveTo(bannerX + bannerR, bannerY);
    ctx.lineTo(bannerX + bannerW - bannerR, bannerY);
    ctx.quadraticCurveTo(bannerX + bannerW, bannerY, bannerX + bannerW, bannerY + bannerR);
    ctx.lineTo(bannerX + bannerW, bannerY + bannerH - bannerR);
    ctx.quadraticCurveTo(bannerX + bannerW, bannerY + bannerH, bannerX + bannerW - bannerR, bannerY + bannerH);
    ctx.lineTo(bannerX + bannerR, bannerY + bannerH);
    ctx.quadraticCurveTo(bannerX, bannerY + bannerH, bannerX, bannerY + bannerR);
    ctx.lineTo(bannerX, bannerY + bannerR);
    ctx.quadraticCurveTo(bannerX, bannerY, bannerX + bannerR, bannerY);
    ctx.closePath();

    ctx.fillStyle = '#f0fdf4';
    ctx.fill();
    ctx.lineWidth = 2;
    ctx.setLineDash([8, 6]);
    ctx.strokeStyle = '#22c55e';
    ctx.stroke();
    ctx.restore();

    // Discount Text
    ctx.textAlign = 'center';
    ctx.fillStyle = '#15803d';
    ctx.font = 'bold 44px "Segoe UI", Arial, sans-serif';
    ctx.fillText(this.getDiscountText(coupon), 400, 240);

    // 7. QR Code Image
    const qrImg = new Image();
    await new Promise<void>((resolve, reject) => {
      qrImg.onload = () => resolve();
      qrImg.onerror = reject;
      qrImg.src = qrDataUrl;
    });

    const qrSize = 380;
    const qrX = (800 - qrSize) / 2;
    const qrY = 305;

    // QR container box
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(qrX - 10, qrY - 10, qrSize + 20, qrSize + 20);
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#e2e8f0';
    ctx.strokeRect(qrX - 10, qrY - 10, qrSize + 20, qrSize + 20);

    ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);

    // 8. 12-Character Voucher Code Badge
    const codeY = 745;
    ctx.fillStyle = '#f1f5f9';
    const codeW = 460;
    const codeH = 64;
    const codeX = (800 - codeW) / 2;
    ctx.beginPath();
    ctx.roundRect(codeX, codeY, codeW, codeH, 12);
    ctx.fill();

    ctx.fillStyle = '#0f172a';
    ctx.font = 'bold 32px monospace';
    ctx.fillText(this.formatCode(coupon.code), 400, codeY + 44);

    // 9. Expiration & Usage
    ctx.fillStyle = '#475569';
    ctx.font = '600 20px "Segoe UI", Arial, sans-serif';
    const expiryText = `Valid Until: ${this.formatDate(coupon.expiresAt)} (Pacific Time)`;
    ctx.fillText(expiryText, 400, 860);

    // Brand scope line (names come from the brands collection, or "All brands")
    ctx.fillStyle = '#1e293b';
    ctx.font = '600 20px "Segoe UI", Arial, sans-serif';
    ctx.fillText(`Valid for: ${this.getBrandLabel(coupon)}`, 400, 836);

    const usageRuleText = coupon.usageType === 'single_use' 
      ? 'Usage: Single Use Only' 
      : `Usage: Multi-Use (${coupon.maxRedemptions} redemptions max)`;
    ctx.fillStyle = '#64748b';
    ctx.font = '500 18px "Segoe UI", Arial, sans-serif';
    ctx.fillText(usageRuleText, 400, 885);

    if (coupon.recipientName && coupon.recipientName !== 'Printed') {
      ctx.fillStyle = '#1e293b';
      ctx.font = '600 18px "Segoe UI", Arial, sans-serif';
      ctx.fillText(`Issued to: ${coupon.recipientName}`, 400, 920);
    }

    // 10. Divider & Instructions
    ctx.strokeStyle = '#f1f5f9';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(100, 950);
    ctx.lineTo(700, 950);
    ctx.stroke();

    ctx.fillStyle = '#94a3b8';
    ctx.font = '15px "Segoe UI", Arial, sans-serif';
    ctx.fillText('How to use: Tap "Coupon" on the payment screen and hold this QR code up to the camera,', 400, 985);
    ctx.fillText('or you can also manually enter the 12-digit code shown above.', 400, 1010);
    ctx.fillText(`© ${this.getIssuerName(coupon)}. All rights reserved.`, 400, 1035);

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Canvas blob generation failed'));
      }, 'image/png');
    });
  }

  /**
   * Downloads a ZIP file containing rendered PNG voucher cards for all selected coupons
   */
  async downloadSelectedZippedVouchers() {
    const selectedList = this.allCoupons.filter(c => this.selectedCouponCodes.has(c.code));
    if (selectedList.length === 0) {
      alert('Please select at least one coupon to download.');
      return;
    }

    this.isDownloadingZip = true;
    this.zipProgressText = `Preparing ${selectedList.length} vouchers...`;

    try {
      const zip = new JSZip();
      const folder = zip.folder('PhotoLavie_Coupons') || zip;

      // Render each voucher card
      for (let i = 0; i < selectedList.length; i++) {
        const coupon = selectedList[i];
        this.zipProgressText = `Generating voucher ${i + 1} of ${selectedList.length} (${coupon.code})...`;

        const blob = await this.renderVoucherCardToBlob(coupon);
        const fileName = `${coupon.code}.png`;
        folder.file(fileName, blob);
      }

      this.zipProgressText = 'Compressing into ZIP archive...';
      const zipBlob = await zip.generateAsync({ type: 'blob' }, (metadata) => {
        this.zipProgressText = `Compressing: ${Math.round(metadata.percent)}%`;
      });

      // Trigger browser download
      const downloadUrl = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      const nowStr = new Date().toISOString().slice(0, 10);
      link.href = downloadUrl;
      link.download = `Coupons_${nowStr}_${selectedList.length}items.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);

    } catch (err: any) {
      console.error('Error generating coupon zip:', err);
      alert('An error occurred while generating the ZIP file. Please try again.');
    } finally {
      this.isDownloadingZip = false;
      this.zipProgressText = '';
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscKey(event: Event) {
    if (this.isModalOpen) this.cancelEdit();
    if (this.isBulkModalOpen) this.closeBulkModal();
    if (this.isQrModalOpen) this.closeQrModal();
    if (this.isEmailModalOpen) this.closeEmailModal();
    if (this.isRedemptionModalOpen) this.closeRedemptionModal();
  }
}
